import PropTypes from "prop-types"
import React, { useContext } from "react"
import { FormProvider, useForm } from "react-hook-form"
import { useMutation } from "react-query"
import { parseSavedLateFee } from "src/main/Billing/Items/helpers"
import { SharedBillingContext } from "src/main/Billing/SharedBillingContext"
import LateFeeSettings from "src/main/Contracts/ContractsForm/shared/LateFeeSettings"
import { formatLateFeeSettingsDataForSubmission } from "src/main/Contracts/ContractsForm/utils"

import Button from "src/components/Button"
import Form from "src/components/Form"
import Modal from "src/components/Modal"

import {
  createReservationLateFee,
  deleteReservationLateFee,
  updateReservationLateFee,
} from "src/api/Billing/Items"

const EditInstallmentLateFeesModal = ({ onClose, useNewTaxExperience }) => {
  const { lateFee, reservationId, setLateFee } =
    useContext(SharedBillingContext)
  const initialLateFeeSettings = lateFee ? [parseSavedLateFee(lateFee)] : []

  const methods = useForm({
    defaultValues: {
      lateFeeSettings: initialLateFeeSettings,
    },
  })

  const {
    handleSubmit,
    setError,
    formState: { errors },
  } = methods

  const {
    mutate: createReservationLateFeeMutation,
    isLoading: isLoadingCreateReservationLateFee,
  } = useMutation(createReservationLateFee, {
    onSuccess: (data) => {
      setLateFee(data)
      onClose()
    },
    onError: () => {
      setError("root.serverError", {
        message:
          "There was a problem creating the late fee. Please try again or contact Dockwa support at mayday@dockwa.com",
      })
    },
  })

  const {
    mutate: updateReservationLateFeeMutation,
    isLoading: isLoadingUpdateReservationLateFee,
  } = useMutation(updateReservationLateFee, {
    onSuccess: (data) => {
      setLateFee(data)
      onClose()
    },
    onError: () => {
      setError("root.serverError", {
        message:
          "There was a problem updating the late fee. Please try again or contact Dockwa support at mayday@dockwa.com",
      })
    },
  })

  const {
    mutate: deleteReservationLateFeeMutation,
    isLoading: isLoadingDeleteReservationLateFee,
  } = useMutation(deleteReservationLateFee, {
    onSuccess: () => {
      setLateFee(null)
      onClose()
    },
    onError: () => {
      setError("root.serverError", {
        message:
          "There was a problem removing the late fee. Please try again or contact Dockwa support at mayday@dockwa.com",
      })
    },
  })

  const onSubmit = (formData) => {
    const formattedLateFeeSettings = formatLateFeeSettingsDataForSubmission(
      formData.lateFeeSettings
    )
    const lateFeeSettingsUnchanged =
      initialLateFeeSettings.length === formattedLateFeeSettings.length &&
      Object.keys(initialLateFeeSettings).every(
        (key) =>
          initialLateFeeSettings[key] === formData.lateFeeSettings[0][key]
      )

    // Nothing changed, just close
    if (lateFeeSettingsUnchanged) {
      return onClose(false)
    }

    const data =
      formattedLateFeeSettings.length > 0
        ? {
            grace_period_days: formattedLateFeeSettings[0].grace_period_days,
            product_id: formattedLateFeeSettings[0].product_id,
            amount: formattedLateFeeSettings[0].amount,
            percent_amount: formattedLateFeeSettings[0].percentage,
          }
        : null
    if (data) {
      if (initialLateFeeSettings.length === 0) {
        createReservationLateFeeMutation({ reservationId, data })
      } else {
        updateReservationLateFeeMutation({ reservationId, data })
      }
    } else {
      deleteReservationLateFeeMutation({ reservationId })
    }
  }

  const isLoadingALateFeeMutation =
    isLoadingCreateReservationLateFee ||
    isLoadingUpdateReservationLateFee ||
    isLoadingDeleteReservationLateFee

  return (
    <Modal isOpen onClose={onClose} maxSize="medium">
      <FormProvider {...methods}>
        <Form onSubmit={handleSubmit(onSubmit)}>
          <Modal.Header title="Edit late fees" />
          <Modal.Body>
            <LateFeeSettings useNewTaxExperience={useNewTaxExperience} />
            <Form.Error>
              {errors?.root?.serverError && (
                <div>{errors.root.serverError.message}</div>
              )}
            </Form.Error>
          </Modal.Body>
          <Modal.Footer>
            <div className="flex justify-end space-x-2">
              <Button variant="tertiary" onClick={onClose}>
                Cancel
              </Button>
              <Button
                isLoading={isLoadingALateFeeMutation}
                disabled={isLoadingALateFeeMutation}
                variant="primary"
                type="submit"
              >
                Save
              </Button>
            </div>
          </Modal.Footer>
        </Form>
      </FormProvider>
    </Modal>
  )
}

EditInstallmentLateFeesModal.propTypes = {
  onClose: PropTypes.func.isRequired,
  useNewTaxExperience: PropTypes.bool.isRequired,
}

export default EditInstallmentLateFeesModal
